/** Renders a run's output, following the tail unless the reader has scrolled away. */

import { useEffect, useRef, useState } from "react";

import type { ConsoleLine } from "@/hooks/useRunConsole";

function toneOf(message: string): string {
  const lower = message.toLowerCase();
  if (
    lower.includes("traceback") ||
    lower.startsWith("error") ||
    lower.includes(" error:") ||
    lower.includes("exception")
  ) {
    return "console__line--error";
  }
  if (lower.startsWith("warning") || lower.includes(" warn")) {
    return "console__line--warn";
  }
  return "";
}

export function Console({
  lines,
  streaming,
  complete,
  error,
}: {
  lines: ConsoleLine[];
  streaming: boolean;
  complete: boolean;
  error?: string;
}) {
  const outputRef = useRef<HTMLPreElement>(null);
  const [follow, setFollow] = useState(true);

  useEffect(() => {
    if (!follow) return;
    const element = outputRef.current;
    if (element) element.scrollTop = element.scrollHeight;
  }, [lines, follow]);

  // Scrolling up is how someone says "stop moving, I am reading this".
  const onScroll = () => {
    const element = outputRef.current;
    if (!element) return;
    const atBottom =
      element.scrollHeight - element.scrollTop - element.clientHeight < 40;
    setFollow(atBottom);
  };

  return (
    <div className="console">
      <div className="console__bar">
        <span>
          {streaming ? "Streaming" : complete ? "Complete" : "Console"}
          {lines.length > 0 && ` · ${lines.length} lines`}
        </span>
        <label className="console__follow">
          <input
            type="checkbox"
            checked={follow}
            onChange={(event) => setFollow(event.target.checked)}
          />
          Follow output
        </label>
      </div>

      <pre className="console__output" ref={outputRef} onScroll={onScroll} tabIndex={0}>
        {lines.length === 0 ? (
          <div className="console__empty">
            {error ?? (streaming ? "Waiting for the first line…" : "No output.")}
          </div>
        ) : (
          lines.map((line, index) => (
            <span key={`${line.ts}-${index}`} className={`console__line ${toneOf(line.message)}`}>
              {line.message}
            </span>
          ))
        )}
      </pre>
    </div>
  );
}
